/**
 * hexRendering.js - Pure functions for hex tile rendering
 *
 * Handles:
 * - Creating layered hex tiles with borders
 * - Color manipulation (saturation, hue)
 * - Hex geometry generation
 */

import * as PIXI from 'pixi.js';
import { getHexPoints } from '../../utils/hexGrid';

/**
 * Boost saturation of a hex color by a multiplier
 * @param {number} color - Color as 0xRRGGBB
 * @param {number} factor - Saturation multiplier (default 1.4)
 * @returns {number} Modified color as 0xRRGGBB
 */
export function boostSaturation(color, factor = 1.4) {
  // Ensure color is a valid non-negative number
  if (typeof color !== 'number' || color < 0 || !isFinite(color)) {
    return 0x504a45; // fallback gray
  }
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const gray = (r + g + b) / 3;
  // Clamp values between 0 and 255
  const newR = Math.max(0, Math.min(255, Math.round(gray + (r - gray) * factor)));
  const newG = Math.max(0, Math.min(255, Math.round(gray + (g - gray) * factor)));
  const newB = Math.max(0, Math.min(255, Math.round(gray + (b - gray) * factor)));
  return (newR << 16) | (newG << 8) | newB;
}

/**
 * Convert CSS color string to hex number
 * @param {string} colorStr - CSS color string (e.g., "#ff0000")
 * @returns {number} Color as 0xRRGGBB
 */
export function colorToNum(colorStr) {
  if (typeof colorStr === 'number') return colorStr;
  if (colorStr.startsWith('#')) {
    return parseInt(colorStr.slice(1), 16);
  }
  return 0x504a45; // fallback
}

/**
 * Create a layered hex tile with fill, inner highlights, and border
 * @param {number} size - Hex size in pixels
 * @param {number} baseColor - Base fill color as 0xRRGGBB
 * @param {number} borderColor - Border color as 0xRRGGBB
 * @param {number} borderWidth - Border width in pixels
 * @param {boolean} isHighlighted - Whether to add extra glow border (for player position)
 * @param {number} fadeAlpha - Overall alpha multiplier (0-1)
 * @returns {PIXI.Graphics} Layered hex graphics
 */
export function createLayeredHex(size, baseColor, borderColor, borderWidth = 1, isHighlighted = false, fadeAlpha = 1) {
  const g = new PIXI.Graphics();
  // Ensure baseColor is valid before calculations
  const safeBaseColor = (typeof baseColor === 'number' && baseColor >= 0 && isFinite(baseColor)) ? baseColor : 0x504a45;
  const safeBorderColor = (typeof borderColor === 'number' && borderColor >= 0 && isFinite(borderColor)) ? borderColor : 0x3a3530;
  const lighterColor = ((safeBaseColor & 0xfefefe) >> 1) + 0x505050;

  // Base fill - apply fade alpha for Great Work placement mode
  g.poly(getHexPoints(size - 1));
  g.fill({ color: safeBaseColor, alpha: fadeAlpha });

  // Inner highlight layer
  g.poly(getHexPoints(size - 4));
  g.fill({ color: lighterColor, alpha: 0.15 * fadeAlpha });

  // Center shine
  g.poly(getHexPoints(size - 8));
  g.fill({ color: 0xffffff, alpha: 0.08 * fadeAlpha });

  // Border with smooth joins
  g.poly(getHexPoints(size - 1));
  g.stroke({ color: safeBorderColor, width: borderWidth, alpha: fadeAlpha, join: 'round' });

  // Extra glow border if highlighted
  if (isHighlighted) {
    g.poly(getHexPoints(size + 1));
    g.stroke({ color: safeBorderColor, width: 1, alpha: 0.3 * fadeAlpha, join: 'round' });
  }

  return g;
}

/**
 * Create a hex border graphic (for elevated borders on border layer)
 * @param {number} size - Hex size in pixels
 * @param {number} color - Border color as 0xRRGGBB
 * @param {number} width - Border width in pixels
 * @param {boolean} isHighlighted - Whether to add extra glow (for player position)
 * @param {number} fadeAlpha - Overall alpha multiplier (0-1)
 * @returns {PIXI.Graphics} Border graphics
 */
export function createHexBorder(size, color, width, isHighlighted = false, fadeAlpha = 1.0) {
  const g = new PIXI.Graphics();

  // Main border
  g.poly(getHexPoints(size - 1));
  g.stroke({ color, width, alpha: fadeAlpha, join: 'round' });

  // Extra glow border if highlighted (player position)
  if (isHighlighted) {
    g.poly(getHexPoints(size + 1));
    g.stroke({ color, width: 1, alpha: 0.3 * fadeAlpha, join: 'round' });
  }

  return g;
}

/**
 * Create a circular indicator background
 * @param {number} radius - Circle radius in pixels
 * @param {number} color - Fill color as 0xRRGGBB
 * @param {number} alpha - Fill alpha (0-1)
 * @returns {PIXI.Graphics} Circle graphics
 */
export function createCircleIndicator(radius, color, alpha = 0.4) {
  const g = new PIXI.Graphics();
  g.circle(0, 0, radius);
  g.fill({ color, alpha });
  return g;
}
